import type { ParticipantBroadcastUpdateEnvelope, ParticipantBroadcastUpdatePayload } from "../realtime/topics";

type BroadcastPayload = {
  id?: string;
  broadcast_id?: string;
  type?: string;
  title?: string;
  body?: string;
  icon?: string;
  action_url?: string;
  persistent?: boolean;
};

const seenBroadcasts = new Set<string>();

function canBootBroadcasts(): boolean {
  const role = String(document.body?.dataset.appRole || "").trim();
  return role === "participant" || role === "volunteer";
}

function broadcastVariant(kind: string): "success" | "error" | "warning" | "info" {
  switch (String(kind || "").trim()) {
    case "urgent":
      return "warning";
    case "result":
      return "success";
    default:
      return "info";
  }
}

async function postBroadcastMutation(id: string, action: "ack" | "dismiss"): Promise<void> {
  await fetch(`/api/broadcasts/${encodeURIComponent(id)}/${action}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
}

function showBroadcast(payload: BroadcastPayload): void {
  const id = String(payload.id || "").trim();
  if (!id || seenBroadcasts.has(id)) return;
  seenBroadcasts.add(id);

  const toastAPI = window.WMUToast;
  if (!toastAPI) return;
  const variant = broadcastVariant(String(payload.type || ""));
  const options = {
    title: String(payload.title || "").trim(),
    variant,
    icon: String(payload.icon || "").trim() || (variant === "warning" ? "⚠" : "📢"),
    duration: payload.persistent ? 0 : 8000,
    persistent: payload.persistent === true,
    haptic: payload.persistent ? "warning" : "light",
  };
  const message = String(payload.body || "").trim();
  const toast = typeof toastAPI.show === "function"
    ? toastAPI.show(message, options)
    : (typeof toastAPI === "function" ? toastAPI(message, options) : null);

  void postBroadcastMutation(id, "ack");

  if (toast instanceof HTMLElement) {
    toast.style.cursor = "pointer";
    toast.addEventListener("click", () => {
      void postBroadcastMutation(id, "dismiss");
      const actionURL = String(payload.action_url || "").trim();
      if (actionURL.startsWith("/")) {
        window.location.assign(actionURL);
      }
    }, { once: true });
  }
}

async function fetchPendingBroadcasts(): Promise<void> {
  const response = await fetch("/api/broadcasts/pending", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) return;
  const payload = await response.json() as { data?: BroadcastPayload[] };
  const items: BroadcastPayload[] = Array.isArray(payload?.data) ? payload.data : [];
  items.forEach((item: BroadcastPayload) => showBroadcast(item));
}

function bootBroadcastStream(): void {
  if (!("EventSource" in window)) return;
  try {
    const source = new EventSource("/api/broadcasts/stream", { withCredentials: true });
    source.addEventListener("wm-broadcast", (event: MessageEvent) => {
      try {
        showBroadcast(JSON.parse(String(event.data || "{}")) as BroadcastPayload);
      } catch (_error) {
        // Ignore malformed payloads.
      }
    });
  } catch (_error) {
    // Poll fallback will still pick up broadcasts.
  }
}

function bootBroadcasts(): void {
  if (!canBootBroadcasts()) return;
  void fetchPendingBroadcasts();
  bootBroadcastStream();
  document.addEventListener("wmu:broadcast-update", (event: Event) => {
    const custom = event as CustomEvent<ParticipantBroadcastUpdateEnvelope>;
    showBroadcast((custom.detail?.payload || {}) as ParticipantBroadcastUpdatePayload & BroadcastPayload);
  });
  window.setInterval(() => {
    void fetchPendingBroadcasts();
  }, 45000);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      void fetchPendingBroadcasts();
    }
  });
}

document.addEventListener("DOMContentLoaded", bootBroadcasts);
